'use client';

// Root boundary. src/app/error.tsx is a *segment* boundary: it renders inside
// RootLayout and Providers, so a throw in the layout itself or anywhere in the
// provider stack (SessionProvider, AuthProvider, LazyMotion, SmoothScroll)
// escapes it and used to land on Next's stock white "Application error" page —
// unstyled, and with no crash-log entry. This file replaces the root layout, so
// it brings its own <html>/<body> and its own stylesheet import.
import './globals.css';

import * as React from 'react';
import { Button } from '@/components/ui/button';
import { SystemNotice } from '@/components/murkoff/system-notice';
import { pushCrashLog } from '@/lib/crash-log';

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  // Same pre-paint logging contract as the segment boundary, in an insertion
  // effect so the render body stays pure and a storage failure can never take
  // the crash screen down with it.
  const logged = React.useRef(false);
  React.useInsertionEffect(() => {
    if (logged.current) return;
    logged.current = true;
     
    console.error('Root application error:', error);
    try {
      pushCrashLog({
        t: Date.now(),
        type: 'boundary',
        message: error.message,
        digest: error.digest,
        stack: error.stack,
      });
    } catch {
      // Best effort only.
    }
  }, [error]);

  // The inline font stack is deliberate. global-error REPLACES the root layout,
  // so next/font's variable classes are never applied here — and Tailwind
  // preflight's `font-family: var(--font-sans), …` on <html> becomes invalid
  // when that custom property is undefined, taking its comma fallbacks with it
  // and leaving the browser's serif default. Colours still resolve: globals.css
  // is imported above.
  return (
    <html lang="en" style={{ fontFamily: 'ui-sans-serif, system-ui, sans-serif' }}>
      <body className="bg-background text-foreground antialiased">
        <SystemNotice
          title="Containment Failure"
          subtitle="Trial Environment Did Not Initialise"
          accessStateLabel="OFFLINE"
          errorCode={error.digest ? `MK-SYS-503-${error.digest}` : 'MK-SYS-503'}
          division="Infrastructure Stability & Containment Division"
        >
          <div role="alert" className="space-y-4 text-sm text-muted-foreground">
            <p className="text-sm text-muted-foreground/90 leading-relaxed">
              The console failed before any trial data could be loaded. Murkoff systems have
              suspended this session entirely; no submission, directive or queue state was
              recorded.
            </p>

            <p className="text-xs text-muted-foreground leading-relaxed">
              Reload to re-establish the link. If the failure repeats, notify the trial
              administrator and include the error code above.
            </p>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <Button
              variant="destructiveOutline"
              className="text-xs tracking-[0.2em] uppercase"
              onClick={() => reset()}
            >
              Attempt Recovery
            </Button>

            {/* A plain href, not the router: the app tree never mounted, so a
                client navigation would re-enter the same broken providers —
                which is exactly what next/link would do here. */}
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a
              href="/"
              className="inline-flex items-center justify-center rounded-md bg-destructive px-6 py-2 text-xs uppercase tracking-[0.2em] text-destructive-foreground hover:bg-destructive/90"
            >
              Return to main console
            </a>
          </div>
        </SystemNotice>
      </body>
    </html>
  );
}
